import { insightStory } from "../../../shared/presentation/insight-story.js";
import { useCallback, useEffect, useRef, useState } from "react";
import type { RivalInsight } from "../../../shared/presentation/rival-insight.js";
import { ApprovedHeading } from "../../shell/approved-primitives.js";
import "../../styles/rival-insights-carousel.css";

export function RivalInsightsCarousel({
  findings,
  onOpen,
  onAll,
}: {
  findings: RivalInsight[];
  onOpen(finding: RivalInsight): void;
  onAll?(): void;
}): React.JSX.Element | null {
  const count = findings.length;
  const { track, setFocused, setHovered, active, setActive, move } =
    useInsightRotation(count);
  if (!count) return <EmptyInsights onAll={onAll} />;
  return (
    <section
      className="fr-insight-carousel"
      aria-label="Rival insights"
      aria-roledescription="carousel"
      onFocusCapture={(event) => {
        if ((event.target as HTMLElement).matches(":focus-visible"))
          setFocused(true);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setFocused(false);
      }}
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") setHovered(true);
      }}
      onPointerLeave={() => setHovered(false)}
      onPointerDown={() => setHovered(true)}
      onPointerUp={(event) => {
        if (event.pointerType !== "mouse") setHovered(false);
      }}
      onPointerCancel={() => setHovered(false)}
    >
      <ApprovedHeading
        title="Insights"
        action={onAll ? "View all" : undefined}
        onAction={onAll}
      />
      <div
        ref={track}
        className="fr-insight-track"
        onScroll={() => {
          const element = track.current;
          if (element)
            setActive(
              Math.min(
                count - 1,
                Math.round(element.scrollLeft / (element.clientWidth + 12)),
              ),
            );
        }}
      >
        {findings.map((finding, index) => (
          <button
            key={`${finding.relationId}:${JSON.stringify(finding.context)}`}
            className="fr-insight-slide"
            onClick={() => onOpen(finding)}
            aria-label={finding.text}
          >
            <InsightNumbers finding={finding} />
            <span className="sr-only">
              {index + 1} of {count}
            </span>
          </button>
        ))}
      </div>
      <CarouselControls
        count={count}
        active={active}
        onMove={(index) => {
          move(index);
        }}
      />
    </section>
  );
}

export function InsightNumbers({
  finding,
}: {
  finding: RivalInsight;
}): React.JSX.Element {
  return (
    <>
      <strong className="fr-insight-story">{insightStory(finding)}</strong>
      <small>{finding.visual?.names.join(" · ")}</small>
      <small>
        {finding.visual?.sample ?? finding.basis.split(" · ")[0]} ·{" "}
        {finding.filters.period === "year" ? finding.filters.year : "All Time"}
      </small>
    </>
  );
}

function useInsightRotation(count: number) {
  const track = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPlaying(!motion.matches);
    const change = () => setPlaying(!motion.matches);
    motion.addEventListener("change", change);
    return () => motion.removeEventListener("change", change);
  }, []);
  useEffect(() => {
    const element = track.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!!entry?.isIntersecting),
      { threshold: 0.6 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [count]);
  const move = useCallback((index: number) => {
    const element = track.current,
      child = element?.children.item(index) as HTMLElement | null;
    if (!element || !child) return;
    element.scrollTo({
      left: child.offsetLeft - (element.children[0] as HTMLElement).offsetLeft,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
    setActive(index);
  }, []);
  useEffect(() => {
    if (!playing || !visible || hovered || focused || count < 2) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) move((active + 1) % count);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [playing, visible, hovered, focused, active, count, move]);
  return { track, setFocused, setHovered, active, setActive, move };
}

function CarouselControls({
  count,
  active,
  onMove,
}: {
  count: number;
  active: number;
  onMove(index: number): void;
}) {
  if (count < 2) return null;
  return (
    <div className="fr-insight-controls">
      <span>
        {Math.min(active + 1, count)} / {count}
      </span>
      <button
        aria-label="Previous insight"
        onClick={() => onMove((active - 1 + count) % count)}
      >
        ‹
      </button>
      <button
        aria-label="Next insight"
        onClick={() => onMove((active + 1) % count)}
      >
        ›
      </button>
    </div>
  );
}

function EmptyInsights({ onAll }: { onAll?: () => void }) {
  return (
    <section className="fr-insight-carousel">
      <ApprovedHeading
        title="Insights"
        action={onAll ? "View all" : undefined}
        onAction={onAll}
      />
      <p className="rc-empty">Play together to discover your edge.</p>
    </section>
  );
}
